import * as z from "zod/mini";
import type { ConfigValidationFieldError } from "@moonpay/platform-protocol/models/connection";
import {
  BOOT_FORCE_ERROR_SCENARIOS,
  OTP_FORCE_ERROR_SCENARIOS,
  CONNECTION_FORCE_ERROR_SCENARIOS,
} from "@connect/data/mocks/force-error-scenarios";
import {
  apiTargetHeader,
  devBaseSchema,
  parseFrameConfig,
  themeBaseSchema,
} from "../../lib/parse-frame-config";

// --- Public params (documented in Mintlify) ---
const publicSchema = z.extend(themeBaseSchema, {
  clientToken: z._default(z.string(), ""),
  publicKey: z._default(z.string(), ""),
  channelId: z._default(z.string(), ""),
  // Auto focus the OTP input when the frame opens ("true" | "false", default "true").
  // Invalid/empty values fall back to the default rather than crashing the frame.
  autoFocus: z.catch(z._default(z.stringbool(), true), true),
});

// --- Dev-only params (ground-control internal) ---
const devSchema = z.object({
  ...devBaseSchema.shape,
  // forceError forwards an x-mock-scenario header to the mock API so QA / tests
  // can drive specific error responses without a real backend.
  forceError: z._default(
    z.enum([
      "",
      ...[
        ...BOOT_FORCE_ERROR_SCENARIOS,
        ...OTP_FORCE_ERROR_SCENARIOS,
        ...CONNECTION_FORCE_ERROR_SCENARIOS,
      ].map((s) => s.value),
    ]),
    "",
  ),
});

const KNOWN_KEYS = new Set([...Object.keys(publicSchema.shape), ...Object.keys(devSchema.shape)]);

export type ConnectConfig = z.infer<typeof publicSchema> & z.infer<typeof devSchema>;

/** Headers shared by every auth HTTP call: apiTarget for proxy routing + optional mock scenario. */
export function authClientHeaders(config: ConnectConfig): Record<string, string> {
  const headers = apiTargetHeader(config);
  if (config.forceError) {
    headers["x-mock-scenario"] = config.forceError;
  }
  return headers;
}

export function parseConfig(search?: string): ConnectConfig {
  const { config, devConfig } = parseFrameConfig({
    publicSchema,
    devSchema,
    knownKeys: KNOWN_KEYS,
    search,
  });
  return { ...config, ...devConfig };
}

/** Fallback cooldown (seconds) when the server omits retryAfterSeconds. */
export const DEFAULT_COOLDOWN_S = 60;

/** Extract retryAfterSeconds from the send-OTP response, falling back to the default. */
export function extractCooldown(data: { retryAfterSeconds: number } | undefined): number {
  return data?.retryAfterSeconds ?? DEFAULT_COOLDOWN_S;
}

const X25519_HEX = /^(0x)?[0-9a-fA-F]{64}$/;

export function validateConfig(config: ConnectConfig): ConfigValidationFieldError[] {
  const errors: ConfigValidationFieldError[] = [];
  if (!config.clientToken) {
    errors.push({
      code: "invalidClientToken",
      message: "clientToken is required",
    });
  }
  if (!config.publicKey) {
    errors.push({
      code: "invalidPublicKey",
      message: "publicKey is required",
    });
  } else if (!X25519_HEX.test(config.publicKey)) {
    errors.push({
      code: "invalidPublicKey",
      message: "publicKey must be 64 hex characters (32 bytes) representing an X25519 public key",
    });
  }
  return errors;
}
